<template>
    <div ref="pieChartContainer" class="pie-chart-container"></div>
</template>

<script>
import * as echarts from "echarts";
import { generateGrayColorPalette, getPrimaryBlueColor } from "../../utils/colorUtils.js";
import { formatSignificantTwoDigitsGrouped } from "@src/utils/currencyUtils.js";

export default {
    name: "DashboardBillPieChart",
    props: {
        data: { type: Array, default: () => [] },
        dashboardData: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            chart: null,
        };
    },
    mounted() {
        this.initChart();
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        if (this.chart) {
            this.chart.dispose();
        }
    },
    watch: {
        data: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.updateChart();
                    }
                });
            },
            deep: true,
        },
        dashboardData: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.updateChart();
                    }
                });
            },
            deep: true,
        },
    },
    methods: {
        formatAmount(value) {
            const num = Number(value);
            if (isNaN(num) || num === 0) return "";

            if (num >= 100000000) {
                const eok = Math.floor(num / 100000000);
                const man = Math.floor((num % 100000000) / 10000);
                if (man > 0) {
                    return `${eok}억 ${man}만원`;
                } else {
                    return `${eok}억원`;
                }
            } else if (num >= 10000) {
                const man = Math.round(num / 10000);
                return `${man}만원`;
            } else {
                return `${Math.round(num).toLocaleString()}원`;
            }
        },
        initChart() {
            if (!this.$refs.pieChartContainer) {
                return;
            }

            this.chart = echarts.init(this.$refs.pieChartContainer);
            this.updateChart();
        },
        handleResize() {
            if (this.chart) {
                this.chart.resize();
            }
        },
        buildPieChartData() {
            // props에서 데이터 받아오기
            const chartData = this.data;

            if (!chartData || chartData.length === 0) {
                return [];
            }

            // 데이터를 복사하고 정렬하여 가장 큰 값을 찾기
            const sortedData = [...chartData].sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0));
            const maxAmount = sortedData[0]?.totalAmount || 0;

            // 가장 큰 값을 가진 항목의 인덱스 찾기
            const maxIndex = chartData.findIndex((item) => item.totalAmount === maxAmount);

            // 회색 팔레트 생성 (가장 큰 항목 제외)
            const grayPalette = generateGrayColorPalette(chartData.length - 1);

            let grayIndex = 0;
            const pieData = chartData.map((item, index) => {
                const amount = item.totalAmount || 0;

                // 가장 큰 영역은 파란색, 나머지는 회색
                const color = index === maxIndex ? getPrimaryBlueColor() : grayPalette[grayIndex++];

                return {
                    value: amount,
                    name: item.custNm || `고객 ${index + 1}`,
                    custNo: item.custNo,
                    itemStyle: {
                        color: color,
                        borderRadius: 4,
                        borderColor: "#fff",
                        borderWidth: 2,
                    },
                };
            });

            return pieData;
        },
        updateChart() {
            if (!this.chart) {
                return;
            }

            // props에서 데이터 받아오기
            const chartData = this.data;

            if (!chartData || chartData.length === 0) {
                return;
            }

            const pieData = this.buildPieChartData();

            // 총 청구 금액 (센터 표시용)
            const totalAmount = this.dashboardData?.totals?.totalAmount || 0;
            const formattedTotalAmount =
                totalAmount && !isNaN(Number(totalAmount))
                    ? formatSignificantTwoDigitsGrouped(Number(totalAmount))
                    : "0원";

            const option = {
                graphic: [
                    {
                        type: "group",
                        left: "center",
                        top: "73%",
                        children: [
                            {
                                type: "text",
                                left: "center",
                                style: {
                                    text: "총 청구 금액",
                                    fill: "#64748b",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    align: "center",
                                },
                            },
                            {
                                type: "text",
                                left: "center",
                                top: 20,
                                style: {
                                    text: formattedTotalAmount,
                                    fill: "#0f172a",
                                    fontSize: 16,
                                    fontWeight: 800,
                                    align: "center",
                                },
                            },
                        ],
                    },
                ],
                tooltip: {
                    trigger: "item",
                    appendTo: "body",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderColor: "#e2e8f0",
                    borderWidth: 1,
                    textStyle: {
                        color: "#1e293b",
                    },
                    formatter: function (info) {
                        const value = info.value || 0;
                        const amount = value ? echarts.format.addCommas(value) + "원" : "-";
                        const percent = info.percent ? info.percent.toFixed(2) + "%" : "-";

                        let title = `<div style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 700; font-size: 12px; color: #1e293b;">${echarts.format.encodeHTML(info.name)}</div>`;
                        let body = '<div style="padding: 8px 12px;">';
                        const items = [
                            { label: "총 청구 금액", value: amount },
                            { label: "비율", value: percent },
                        ];
                        items.forEach((item) => {
                            body += `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; font-size: 11px;">
                                <span style="color: #64748b;">${item.label}</span>
                                <span style="font-weight: 700; color: #1e293b; margin-left: 16px;">${item.value}</span>
                            </div>`;
                        });
                        body += "</div>";
                        return title + body;
                    },
                },
                series: [
                    {
                        name: "고지서",
                        type: "pie",
                        radius: ["60%", "130%"],
                        center: ["50%", "90%"],
                        startAngle: 180,
                        endAngle: 0,
                        avoidLabelOverlap: false,
                        itemStyle: {
                            borderRadius: 4,
                            borderColor: "#fff",
                            borderWidth: 2,
                        },
                        label: {
                            show: true,
                            position: "outside",
                            formatter: (params) => {
                                const amount = params.value || 0;
                                const formattedAmount = formatSignificantTwoDigitsGrouped(amount);
                                return `{name|${params.name}}\n{amount|${formattedAmount}}\n{percent|${params.percent}%}`;
                            },
                            rich: {
                                name: {
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#1e293b",
                                    lineHeight: 16,
                                },
                                amount: {
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#475569",
                                    lineHeight: 14,
                                },
                                percent: {
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#94a3b8",
                                    lineHeight: 14,
                                },
                            },
                        },
                        labelLine: {
                            show: true,
                            length: 20,
                            length2: 15,
                            lineStyle: {
                                color: "#64748b",
                                width: 1,
                                type: "solid",
                            },
                        },
                        emphasis: {
                            label: {
                                show: true,
                                fontSize: 13,
                                fontWeight: 700,
                            },
                            labelLine: {
                                show: true,
                                lineStyle: {
                                    width: 2,
                                },
                            },
                            itemStyle: {
                                shadowBlur: 8,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.2)",
                            },
                        },
                        data: pieData,
                    },
                ],
            };

            this.chart.setOption(option);

            // 클릭 이벤트 추가
            this.chart.off("click");
            this.chart.on("click", (params) => {
                if (params.data && params.data.custNo) {
                    this.$emit("select-customer", params.data.custNo);
                }
            });
        },
    },
};
</script>

<style scoped>
.pie-chart-container {
    width: 100%;
    height: 100%;
    min-height: 0;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
